import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { isNullOrUndefined } from 'is-what';
import moment from 'moment-timezone';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { StringConstants } from '../../constants/string-constants';
import { CurrenyTransformService } from '../../shared/services/curreny-transform.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadPdfService {
  private megaHeaderWidth: any = [50, 100, 100, 100, 100, 85, 85, 70];
  public loanPurposeResResolved: any;
  public occupancyStatusResResolved: any;
  public channelResResolved: any;
  public propertyTypeResResolved: any;

  private datePipe = inject(DatePipe);
  private convert = inject(CurrenyTransformService);
  public fmLoadingPageService = inject(FmLoadingPageService);

  parseMegaDetails(megaDetailsList) {
    let dealAmount: string = this.convert.currencyTransform(megaDetailsList.dealAmount.toString());
    if (!dealAmount.includes('.')) {
      dealAmount = dealAmount + '.00';
    }
    const megaDetailsData = [];
    megaDetailsData.push({ text: megaDetailsList.megaPrefix, alignment: 'left' });
    megaDetailsData.push({
      text: this.datePipe.transform(megaDetailsList.megaIssueDate, 'MM-dd-yyyy'),
      alignment: 'left',
    });
    megaDetailsData.push({
      text: this.datePipe.transform(megaDetailsList.maturityDate, 'MM-yyyy'),
      alignment: 'left',
    });
    megaDetailsData.push({ text: megaDetailsList.coupon.toFixed(3), alignment: 'left' });
    megaDetailsData.push({ text: dealAmount, alignment: 'right' });
    megaDetailsData.push({ text: megaDetailsList.fannieIssued + '%', alignment: 'left' });
    megaDetailsData.push({ text: megaDetailsList.freddieIssued + '%', alignment: 'left' });
    megaDetailsData.push({ text: megaDetailsList.monthsToMaturity, alignment: 'left' });

    if (megaDetailsList.subType != null) {
      megaDetailsData.push({ text: megaDetailsList.subType, alignment: 'left' });
    }
    if (megaDetailsList.mfHighCoupon != null) {
      megaDetailsData.push({ text: megaDetailsList.mfHighCoupon, alignment: 'left' });
    }
    if (megaDetailsList.mfLowCoupon != null) {
      megaDetailsData.push({ text: megaDetailsList.mfLowCoupon, alignment: 'left' });
    }
    return megaDetailsData;
  }

  parseMegaHeaders(megaType: string) {
    const headerList: any = [];
    headerList.push({ text: 'Prefix', fillColor: 'lightgrey' });
    headerList.push({ text: 'Issue Date', fillColor: 'lightgrey' });
    headerList.push({ text: 'Maturity Date', fillColor: 'lightgrey' });
    headerList.push({ text: 'WA Net Interest Rate', fillColor: 'lightgrey' });
    headerList.push({ text: 'Pool Amount', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'FNMA Issued (%)', fillColor: 'lightgrey' });
    headerList.push({ text: 'FHLMC Issued (%)', fillColor: 'lightgrey' });
    headerList.push({ text: 'WA Months to Maturity', fillColor: 'lightgrey' });

    if (megaType === 'SFARMMEGA' || megaType === 'MFARMMEGA') {
      headerList.push({ text: 'Subtype', fillColor: 'lightgrey' });
      this.megaHeaderWidth = [50, 70, 70, 100, 100, 85, 85, 70, 50];
    }
    if (megaType === 'MFFIXMEGA') {
      headerList.push({ text: 'High Coupon', fillColor: 'lightgrey' });
      headerList.push({ text: 'Low Coupon', fillColor: 'lightgrey' });
      this.megaHeaderWidth = [50, 60, 60, 100, 'auto', 85, 85, 70, 50, 50];
    }

    return headerList;
  }

  parseCollateralHeaders() {
    const headerList: any = [];

    headerList.push({ text: 'Prefix', fillColor: 'lightgrey' });
    headerList.push({ text: 'Collateral Pool #', fillColor: 'lightgrey' });
    headerList.push({ text: 'CUSIP', fillColor: 'lightgrey' });
    headerList.push({ text: 'WA Net Interest Rate', fillColor: 'lightgrey' });
    headerList.push({ text: 'Issuer', fillColor: 'lightgrey' });
    headerList.push({ text: 'Issue Date', fillColor: 'lightgrey' });
    headerList.push({ text: 'Maturity Date', fillColor: 'lightgrey' });
    headerList.push({
      text: 'Collateral Issuance Contributing UPB',
      fillColor: 'lightgrey',
      alignment: 'right',
    });
    headerList.push({
      text: 'Collateral Current Contributing UPB',
      fillColor: 'lightgrey',
      alignment: 'right',
    });
    headerList.push({ text: 'WA Months to Maturity', fillColor: 'lightgrey' });
    headerList.push({ text: 'WA  Interest Rate at Settlement', fillColor: 'lightgrey' });
    return headerList;
  }

  statisticsQuartileHeaders() {
    const headerList: any = [];

    headerList.push({ text: 'Quartile Level', fillColor: 'lightgrey' });
    headerList.push({ text: 'Mortgage Loan Amount', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Interest Rate', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Net Interest Rate', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Loan Term', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Loan Age', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Loan-To-Value (LTV)', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({
      text: 'Remaining Months To Maturity',
      fillColor: 'lightgrey',
      alignment: 'right',
    });
    headerList.push({
      text: 'Combined Loan-To-Value (CLTV)',
      fillColor: 'lightgrey',
      alignment: 'right',
    });
    headerList.push({ text: 'Borrower Credit Score', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Debt-To-Income (DTI)', fillColor: 'lightgrey', alignment: 'right' });
    return headerList;
  }

  disclosureDetailsHeaders() {
    const headerList: any = [];

    headerList.push({ text: 'ATTRIBUTE', fillColor: 'lightgrey' });
    headerList.push({ text: 'ISSUANCE', fillColor: 'lightgrey', style: 'detailsValue' });
    return headerList;
  }

  parseDisclosureDetails(
    disclosureWaDetailsData,
    disclosureSellerDetailsData,
    monthsToMaturity,
    netInterestRate,
  ) {
    const disclosureDetails = [];
    const disclosureDetailsListTemp = [];
    disclosureDetails.push(this.disclosureDetailsHeaders());

    const detailsHeader = [];
    const detailsValue = [];

    let waMortgageLoanAmount =
      !isNullOrUndefined(disclosureWaDetailsData) &&
      !isNullOrUndefined(disclosureWaDetailsData.waMortgageLoanAmount)
        ? this.convert.currencyTransform(disclosureWaDetailsData.waMortgageLoanAmount.toString())
        : '-';
    let avgMortgageLoanAmount =
      !isNullOrUndefined(disclosureSellerDetailsData) &&
      !isNullOrUndefined(disclosureSellerDetailsData.avgMortgageLoanAmount)
        ? this.convert.currencyTransform(
            disclosureSellerDetailsData.avgMortgageLoanAmount.toString(),
          )
        : '-';
    if (waMortgageLoanAmount && !waMortgageLoanAmount.includes('.')) {
      waMortgageLoanAmount = !isNullOrUndefined(disclosureWaDetailsData)
        ? waMortgageLoanAmount + '.00'
        : '';
    }

    if (avgMortgageLoanAmount && !avgMortgageLoanAmount.includes('.')) {
      avgMortgageLoanAmount = !isNullOrUndefined(disclosureSellerDetailsData)
        ? avgMortgageLoanAmount + '.00'
        : '';
    }

    detailsHeader.push('WA Net Interest Rate');
    detailsHeader.push('WA Interest Rate');
    detailsHeader.push('WA Remaining Months to Maturity');
    detailsHeader.push(' WA Loan Age');
    detailsHeader.push('WA Mortgage Loan Amount');
    detailsHeader.push('WA Debt-To-Income (DTI)');
    detailsHeader.push('WA Loan-To-Value (LTV)');
    detailsHeader.push('WA Borrower Credit Score');
    detailsHeader.push('WA Loan Term');
    detailsHeader.push('Average Mortgage Loan Amount');
    detailsHeader.push('Loan Count');
    detailsHeader.push('Delinquent Loans Purchased (Prior Month UPB)');
    detailsHeader.push('Eligible for Resecuritization');
    detailsHeader.push('Payment Delay Days)');
    detailsHeader.push('Interest Only Security Indicator');
    detailsHeader.push('Prepayment Penality Indicator');
    detailsHeader.push('Reduced Minimum Servicing Indicator');
    detailsHeader.push('Security Data Correction Indicator');
    detailsHeader.push('Security Notification Indicator');
    detailsHeader.push('WA Combined Loan-To-Value (CLTV)');
    detailsHeader.push('Third Party Origination UPB Percent');

    detailsValue.push(netInterestRate.toFixed(3));
    detailsValue.push(
      !isNullOrUndefined(disclosureWaDetailsData) ? disclosureWaDetailsData.waInterestRate : '',
    );
    detailsValue.push(monthsToMaturity);
    detailsValue.push(
      !isNullOrUndefined(disclosureWaDetailsData) ? disclosureWaDetailsData.waLoanAge : '',
    );
    detailsValue.push(waMortgageLoanAmount);
    detailsValue.push('-');
    detailsValue.push(
      !isNullOrUndefined(disclosureWaDetailsData) ? disclosureWaDetailsData.waLoanToValue : '',
    );
    detailsValue.push(
      !isNullOrUndefined(disclosureWaDetailsData)
        ? disclosureWaDetailsData.waBorrowerCreditScore
        : '',
    );
    detailsValue.push(
      !isNullOrUndefined(disclosureSellerDetailsData)
        ? disclosureSellerDetailsData.waOriginalLoanTerm
        : '',
    );
    detailsValue.push(avgMortgageLoanAmount);
    detailsValue.push(
      !isNullOrUndefined(disclosureWaDetailsData) ? disclosureWaDetailsData.loanCount : '',
    );
    detailsValue.push('-');
    detailsValue.push('Yes');
    detailsValue.push(
      !isNullOrUndefined(disclosureSellerDetailsData)
        ? disclosureSellerDetailsData.paymentDelayDays
        : '',
    );
    detailsValue.push(
      !isNullOrUndefined(disclosureSellerDetailsData)
        ? disclosureSellerDetailsData.interestOnlySecurityIndicator
        : '',
    );
    detailsValue.push('-');
    detailsValue.push('-');
    detailsValue.push('-');
    detailsValue.push('-');
    detailsValue.push(
      !isNullOrUndefined(disclosureSellerDetailsData)
        ? disclosureSellerDetailsData.waCombined_LTV
        : '',
    );
    detailsValue.push(
      !isNullOrUndefined(disclosureSellerDetailsData)
        ? disclosureSellerDetailsData.thirdPartyOriginationUPBPercent
        : '',
    );

    detailsHeader.forEach((item, index) => {
      const mBundle: any = {};
      mBundle.attribute = item;
      mBundle.val = detailsValue[index];

      disclosureDetailsListTemp.push(mBundle);
    });

    for (const details of disclosureDetailsListTemp) {
      const disclosureDetailsList = [];
      disclosureDetailsList.push(
        { text: details.attribute },
        { text: details.val, alignment: 'left', style: 'detailsValue' },
      );
      disclosureDetails.push(disclosureDetailsList);
    }
    return disclosureDetails;
  }

  parseCollateralDetails(pooldetails) {
    const collateralData = [];
    collateralData.push(this.parseCollateralHeaders());
    for (const poolDetail of pooldetails) {
      let poolAmount = this.convert.currencyTransform(poolDetail.upbAmount.toString());
      let upbAmountWithFactor = this.convert.currencyTransform(
        poolDetail.upbAmountWithFactor.toString(),
      );
      let issuer = '';
      if (!poolAmount.includes('.')) {
        poolAmount = poolAmount + '.00';
      }
      if (!upbAmountWithFactor.includes('.')) {
        upbAmountWithFactor = upbAmountWithFactor + '.00';
      }
      if (poolDetail.issuer === 'FN') {
        issuer = 'Fannie Mae';
      } else if (poolDetail.issuer === 'FR') {
        issuer = 'Freddie Mac';
      }
      const collateralList = [];
      collateralList.push(
        { text: poolDetail.prefix },
        { text: poolDetail.collateralPoolNumber, alignment: 'left' },
        { text: poolDetail.cusip, alignment: 'left' },
        { text: poolDetail.coupon.toFixed(3), alignment: 'left' },
        { text: issuer, alignment: 'left' },
        {
          text: this.datePipe.transform(poolDetail.issueDate, 'MM-dd-yyyy'),
          alignment: 'left',
        },
        {
          text: this.datePipe.transform(poolDetail.maturityDate, 'MM-yyyy'),
          alignment: 'left',
        },
        { text: poolAmount, alignment: 'right' },
        { text: upbAmountWithFactor, alignment: 'right' },
        { text: poolDetail.mbsWamTerm, alignment: 'left' },
        { text: poolDetail.mbsWacRate.toFixed(3), alignment: 'left' },
      );
      collateralData.push(collateralList);
    }
    return collateralData;
  }

  statisticsHeaders(tableHeader) {
    const headerList: any = [];

    headerList.push({ text: tableHeader, fillColor: 'lightgrey' });
    headerList.push({
      text: 'Aggregate Investor Loan UPB',
      fillColor: 'lightgrey',
      alignment: 'right',
    });
    headerList.push({
      text: 'Percentage Investor Loan UPB',
      fillColor: 'lightgrey',
      alignment: 'right',
    });
    headerList.push({ text: 'Aggregate Loan Count', fillColor: 'lightgrey', alignment: 'right' });
    headerList.push({ text: 'Percentage Loan Count', fillColor: 'lightgrey', alignment: 'right' });
    return headerList;
  }

  parseLoanPurposeData(loanPurposeRes) {
    const loanPurposeData = [];
    loanPurposeData.push(this.statisticsHeaders('Loan Purpose'));
    this.loanPurposeAttributeMapping(loanPurposeRes);
    if (!isNullOrUndefined(this.loanPurposeResResolved)) {
      for (const loanPurpose of this.loanPurposeResResolved) {
        let upbAmount =
          loanPurpose?.aggUPB != null
            ? this.convert.currencyTransform(loanPurpose.aggUPB.toString())
            : '0';

        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }

        const loanPurposeList = [];
        loanPurposeList.push(
          { text: loanPurpose.attributeType },
          { text: upbAmount, alignment: 'right' },
          { text: loanPurpose.pctAggUPB, alignment: 'right' },
          { text: loanPurpose.loanCount, alignment: 'right' },
          { text: loanPurpose.pctLoanCount, alignment: 'right' },
        );
        loanPurposeData.push(loanPurposeList);
      }
    } else {
      const loanPurposeList = [];
      loanPurposeList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      loanPurposeData.push(loanPurposeList);
    }
    return loanPurposeData;
  }

  parseOccupancyStatusData(occupancyStatusRes) {
    const occupancyStatusData = [];
    occupancyStatusData.push(this.statisticsHeaders('Occupancy Status'));
    this.occupancyStatusAttributeMapping(occupancyStatusRes);
    if (!isNullOrUndefined(this.occupancyStatusResResolved)) {
      for (const occupancyStatus of this.occupancyStatusResResolved) {
        let upbAmount = occupancyStatus?.aggUPB
          ? this.convert.currencyTransform(occupancyStatus.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const occupancyStatusList = [];
        occupancyStatusList.push(
          { text: occupancyStatus.attributeType },
          { text: upbAmount, alignment: 'right' },
          { text: occupancyStatus.pctAggUPB, alignment: 'right' },
          { text: occupancyStatus.loanCount, alignment: 'right' },
          { text: occupancyStatus.pctLoanCount, alignment: 'right' },
        );
        occupancyStatusData.push(occupancyStatusList);
      }
    } else {
      const occupancyStatusList = [];
      occupancyStatusList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      occupancyStatusData.push(occupancyStatusList);
    }
    return occupancyStatusData;
  }

  parseChannelData(channelRes) {
    const channelData = [];
    channelData.push(this.statisticsHeaders('Channel'));
    this.channelDetailsAttributeMapping(channelRes);
    if (!isNullOrUndefined(this.channelResResolved)) {
      for (const channel of this.channelResResolved) {
        let upbAmount =
          channel?.aggUPB != null ? this.convert.currencyTransform(channel.aggUPB.toString()) : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const channelList = [];
        channelList.push(
          { text: channel.attributeType },
          { text: upbAmount, alignment: 'right' },
          { text: channel.pctAggUPB, alignment: 'right' },
          { text: channel.loanCount, alignment: 'right' },
          { text: channel.pctLoanCount, alignment: 'right' },
        );
        channelData.push(channelList);
      }
    } else {
      const channelList = [];
      channelList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      channelData.push(channelList);
    }
    return channelData;
  }

  parseStateData(stateRes) {
    const stateData = [];
    stateData.push(this.statisticsHeaders('State'));
    if (!isNullOrUndefined(stateRes)) {
      for (const state of stateRes) {
        let upbAmount = state?.aggUPB
          ? this.convert.currencyTransform(state.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const stateList = [];
        stateList.push(
          { text: state.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: state.pctAggUPB, alignment: 'right' },
          { text: state.loanCount, alignment: 'right' },
          { text: state.pctLoanCount, alignment: 'right' },
        );
        stateData.push(stateList);
      }
    } else {
      const stateList = [];
      stateList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      stateData.push(stateList);
    }

    return stateData;
  }

  parsePropertyTypeData(propertyTypeRes) {
    const propertyTypeData = [];
    propertyTypeData.push(this.statisticsHeaders('Property Type'));
    this.propertyTypeAttributeMapping(propertyTypeRes);
    if (!isNullOrUndefined(this.propertyTypeResResolved)) {
      for (const propertyType of this.propertyTypeResResolved) {
        let upbAmount = propertyType?.aggUPB
          ? this.convert.currencyTransform(propertyType.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const propertyTypeList = [];
        propertyTypeList.push(
          { text: propertyType.attributeType },
          { text: upbAmount, alignment: 'right' },
          { text: propertyType.pctAggUPB, alignment: 'right' },
          { text: propertyType.loanCount, alignment: 'right' },
          { text: propertyType.pctLoanCount, alignment: 'right' },
        );
        propertyTypeData.push(propertyTypeList);
      }
    } else {
      const propertyTypeList = [];
      propertyTypeList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      propertyTypeData.push(propertyTypeList);
    }
    return propertyTypeData;
  }

  parseQuartileCompleteData(quartileCompleteRes) {
    const quartileCompleteData = [];
    let upbAmount: any;
    quartileCompleteData.push(this.statisticsQuartileHeaders());
    if (!isNullOrUndefined(quartileCompleteRes)) {
      for (const quartile of quartileCompleteRes) {
        if (!isNullOrUndefined(quartile.mortgageLoanAmount)) {
          upbAmount = this.convert.currencyTransform(quartile.mortgageLoanAmount.toString());
          if (upbAmount && !upbAmount.includes('.')) {
            upbAmount = upbAmount + '.00';
          }
        }

        const quartileList = [];
        quartileList.push(
          {
            text: !isNullOrUndefined(quartile.attribute) ? quartile.attribute : '',
          },
          { text: upbAmount, alignment: 'right' },
          {
            text: !isNullOrUndefined(quartile.interestRate) ? quartile.interestRate : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.netInterestRate_Current)
              ? quartile.netInterestRate_Current
              : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.loanTerm) ? quartile.loanTerm : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.loanAge) ? quartile.loanAge : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.loanToValue) ? quartile.loanToValue : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.remainingMonthsToMaturity)
              ? quartile.remainingMonthsToMaturity
              : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.combined_LoanToValue)
              ? quartile.combined_LoanToValue
              : '',
            alignment: 'right',
          },
          {
            text: !isNullOrUndefined(quartile.borrowerCreditScore)
              ? quartile.borrowerCreditScore
              : '',
            alignment: 'right',
          },
          { text: quartile.DTI, alignment: 'right' },
        );
        quartileCompleteData.push(quartileList);
      }
    } else {
      const quartileList = [];
      quartileList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );

      quartileCompleteData.push(quartileList);
    }
    return quartileCompleteData;
  }

  parseBorrowerData(borrowerRes) {
    const borrowerData = [];
    borrowerData.push(this.statisticsHeaders('Borrowers'));
    if (!isNullOrUndefined(borrowerRes)) {
      for (const borrower of borrowerRes) {
        let upbAmount = borrower?.aggUPB
          ? this.convert.currencyTransform(borrower.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const borrowerList = [];
        borrowerList.push(
          { text: borrower.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: borrower.pctAggUPB, alignment: 'right' },
          { text: borrower.loanCount, alignment: 'right' },
          { text: borrower.pctLoanCount, alignment: 'right' },
        );
        borrowerData.push(borrowerList);
      }
    } else {
      const borrowerList = [];
      borrowerList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      borrowerData.push(borrowerList);
    }
    return borrowerData;
  }

  parseMtgInsuranceCancelIndData(mtgInsuranceCancelIndRes) {
    const mtgInsuranceCancelIndData = [];
    mtgInsuranceCancelIndData.push(
      this.statisticsHeaders('Mortgage Insurance Cancellation Indicator'),
    );
    if (!isNullOrUndefined(mtgInsuranceCancelIndRes)) {
      for (const mtgInsuranceCancelInd of mtgInsuranceCancelIndRes) {
        let upbAmount = mtgInsuranceCancelInd?.aggUPB
          ? this.convert.currencyTransform(mtgInsuranceCancelInd.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const mtgInsuranceCancelIndList = [];
        mtgInsuranceCancelIndList.push(
          { text: mtgInsuranceCancelInd.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: mtgInsuranceCancelInd.pctAggUPB, alignment: 'right' },
          { text: mtgInsuranceCancelInd.loanCount, alignment: 'right' },
          { text: mtgInsuranceCancelInd.pctLoanCount, alignment: 'right' },
        );
        mtgInsuranceCancelIndData.push(mtgInsuranceCancelIndList);
      }
    } else {
      const mtgInsuranceCancelIndList = [];
      mtgInsuranceCancelIndList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      mtgInsuranceCancelIndData.push(mtgInsuranceCancelIndList);
    }
    return mtgInsuranceCancelIndData;
  }

  parseMtgInsuranceCoverageData(mtgInsuranceCoverageRes) {
    const mtgInsuranceCoverageData = [];
    mtgInsuranceCoverageData.push(this.statisticsHeaders('Mortgage Insurance Coverage'));
    if (!isNullOrUndefined(mtgInsuranceCoverageRes)) {
      for (const mtgInsuranceCoverage of mtgInsuranceCoverageRes) {
        let upbAmount = mtgInsuranceCoverage?.aggUPB
          ? this.convert.currencyTransform(mtgInsuranceCoverage.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const mtgInsuranceCoverageList = [];
        mtgInsuranceCoverageList.push(
          { text: mtgInsuranceCoverage.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: mtgInsuranceCoverage.pctAggUPB, alignment: 'right' },
          { text: mtgInsuranceCoverage.loanCount, alignment: 'right' },
          { text: mtgInsuranceCoverage.pctLoanCount, alignment: 'right' },
        );
        mtgInsuranceCoverageData.push(mtgInsuranceCoverageList);
      }
    } else {
      const mtgInsuranceCoverageList = [];
      mtgInsuranceCoverageList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      mtgInsuranceCoverageData.push(mtgInsuranceCoverageList);
    }
    return mtgInsuranceCoverageData;
  }

  parseNoOfUnitsData(noOfUnitsRes) {
    const NoOfUnitsData = [];
    NoOfUnitsData.push(this.statisticsHeaders('Number Of Units'));
    if (!isNullOrUndefined(noOfUnitsRes)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const noOfUnits of noOfUnitsRes) {
        let upbAmount = noOfUnitsRes?.aggUPB
          ? this.convert.currencyTransform(noOfUnitsRes.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const NoOfUnitsList = [];
        NoOfUnitsList.push(
          { text: noOfUnitsRes.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: noOfUnitsRes.pctAggUPB, alignment: 'right' },
          { text: noOfUnitsRes.loanCount, alignment: 'right' },
          { text: noOfUnitsRes.pctLoanCount, alignment: 'right' },
        );
        NoOfUnitsData.push(NoOfUnitsList);
      }
    } else {
      const NoOfUnitsList = [];
      NoOfUnitsList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      NoOfUnitsData.push(NoOfUnitsList);
    }
    return NoOfUnitsData;
  }

  parseOriginationYearData(originationYearRes) {
    const originationYearData = [];
    originationYearData.push(this.statisticsHeaders('Orig. Year'));
    if (!isNullOrUndefined(originationYearRes)) {
      for (const originationYear of originationYearRes) {
        let upbAmount = originationYear?.aggUPB
          ? this.convert.currencyTransform(originationYear.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const originationYearList = [];
        originationYearList.push(
          { text: originationYear.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: originationYear.pctAggUPB, alignment: 'right' },
          { text: originationYear.loanCount, alignment: 'right' },
          { text: originationYear.pctLoanCount, alignment: 'right' },
        );
        originationYearData.push(originationYearList);
      }
    } else {
      const originationYearList = [];
      originationYearList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      originationYearData.push(originationYearList);
    }
    return originationYearData;
  }

  parseServicerDistData(servicerDistributionRes) {
    const servicerDistributionData = [];
    servicerDistributionData.push(this.statisticsHeaders('Servicer Name'));
    if (!isNullOrUndefined(servicerDistributionRes)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const servicerDistribution of servicerDistributionRes) {
        let upbAmount = servicerDistributionRes?.aggUPB
          ? this.convert.currencyTransform(servicerDistributionRes.aggUPB.toString())
          : '0';
        if (upbAmount && !upbAmount.includes('.')) {
          upbAmount = upbAmount + '.00';
        }
        const servicerDistributionList = [];
        servicerDistributionList.push(
          { text: servicerDistributionRes.recTypeValue },
          { text: upbAmount, alignment: 'right' },
          { text: servicerDistributionRes.pctAggUPB, alignment: 'right' },
          { text: servicerDistributionRes.loanCount, alignment: 'right' },
          { text: servicerDistributionRes.pctLoanCount, alignment: 'right' },
        );
        servicerDistributionData.push(servicerDistributionList);
      }
    } else {
      const servicerDistributionList = [];
      servicerDistributionList.push(
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
        { text: '', style: 'errorDisplay' },
      );
      servicerDistributionData.push(servicerDistributionList);
    }
    return servicerDistributionData;
  }

  public loanPurposeAttributeMapping(loan): any {
    loan?.forEach((item) => {
      switch (item.recTypeValue) {
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_REFINANCE_CASH_OUT:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_REFINANCE_CASH_OUT;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_REFINANCE_NO_CASH_OUT:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_REFINANCE_NO_CASH_OUT;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_REFINANCE_NOT_SPECIFIED:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_REFINANCE_NOT_SPECIFIED;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_PURCHASE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_PURCHASE;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_MODIFIED_LOSS_MITIGATION:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_MODIFIED_LOSS_MITIGATION;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_ERRORCODE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_ERRORCODE;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_CODE_NOT_AVAILABLE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_LOAN_PURPOSE_LABEL_NOT_AVAILABLE;
          break;
        default:
          item.attributeType = '-';
          break;
      }
    });

    this.loanPurposeResResolved = loan;
  }

  public occupancyStatusAttributeMapping(occupancyStatus): void {
    occupancyStatus?.forEach((item) => {
      switch (item.recTypeValue) {
        case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_PRIMARY_HOME:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_PRIMARY_HOME;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_SECONDARY_HOME:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_SECONDARY_HOME;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_INVESTMENT_PROPERTY:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_INVESTMENT_PROPERTY;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_ERRORCODE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_ERRORCODE;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_CODE_NOT_AVAILABLE:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_OCCUPANCY_STATUS_LABEL_NOT_AVAILABLE;
          break;
        default:
          item.attributeType = '-';
          break;
      }
    });

    this.occupancyStatusResResolved = occupancyStatus;
  }

  public channelDetailsAttributeMapping(channel): any {
    channel?.forEach((item) => {
      switch (item.recTypeValue) {
        case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_RETAIL:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_RETAIL;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_BROKER:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_BROKER;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_CORRESPONDENT:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_CORRESPONDENT;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_THIRD_PARTY_ORIGINATION:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_THIRD_PARTY_ORIGINATION;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_ERRORCODE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_ERRORCODE;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_CHANNEL_CODE_NOT_AVAILABLE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_CHANNEL_LABEL_NOT_AVAILABLE;
          break;
        default:
          item.attributeType = '-';
          break;
      }
    });

    this.channelResResolved = channel;
  }

  public propertyTypeAttributeMapping(propertyType): any {
    propertyType?.forEach((item) => {
      switch (item.recTypeValue) {
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_COOPERATIVE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_COOPERATIVE;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_CONDOMINIUM:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_CONDOMINIUM;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_PLANNED_UNIT:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_PLANNED_UNIT;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_SINGLE_FAMILY:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_SINGLE_FAMILY;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_MANUFACTURED_HOUSING:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_MANUFACTURED_HOUSING;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_ERRORCODE:
          item.attributeType = StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_ERRORCODE;
          break;
        case StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_CODE_NOT_AVAILABLE:
          item.attributeType =
            StringConstants.ANALYZER_DISCLOSURE_PROPERTY_TYPE_LABEL_NOT_AVAILABLE;
          break;
        default:
          item.attributeType = '-';
          break;
      }
    });

    this.propertyTypeResResolved = propertyType;
  }

  public downloadREMICPDF(pdfData: any, shelfNumber: string): void {
    const downloadedTime = moment(new Date());

    const downloadErrorReportName =
      'DealStructure_IssueLog-' +
      shelfNumber +
      '_' +
      downloadedTime.format('YYYYMMDDHHmmss') +
      '.pdf';

    pdfMake.addVirtualFileSystem(pdfFonts);

    const dd = {
      pageOrientation: 'portrait',
      pageSize: 'A4',
      pageMargins: [30, 50, 40, 40],
      header: [
        {
          margin: [0, 0, 0, 0],
          columns: [
            {
              image:
                'data:image/png;base64,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',
              width: 700,
              margin: [0, 0],
              height: 50,
              fillColor: '#085280',
            },
          ],
        },
      ],
      footer(currentPage) {
        return {
          margin: 5,
          columns: [
            {
              text: currentPage.toString(),
              margin: [30, 10, 0, 0],
              width: '10%',
            },
          ],
        };
      },
      content: [
        {
          text: 'Structure Upload - Issue Log',
          style: 'structureHeader',
        },
        {
          text: 'Shelf #: ' + shelfNumber,
          style: 'shelfNumber',
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 1,
              x2: 530,
              y2: 1,
              lineWidth: 0.2,
              color: 'lightgrey',
            },
          ],
        },
        pdfData,
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: false,
          margin: [30, 5, 0, 5],
          fillColor: '#085280',
        },
        exclamation: {
          margin: [0, 5, 0, 5],
        },
        structureHeader: {
          fontSize: 24,
          bold: false,
          color: '#ef9024',
          margin: [0, 5, 0, 5],
        },
        shelfNumber: {
          fontSize: 12,
          bold: true,
          margin: [5, 5, 0, 5],
        },
        errorMessage: {
          fontSize: 8,
          margin: [0, 0, 0, 5],
        },
        headerTable: {
          fontSize: 12,
          color: 'white',
          bold: true,
        },
        collateralHeader: {
          fontSize: 14,
          bold: true,
          margin: [0, 15, 0, 2],
        },
        statisticsHeader: {
          fontSize: 12,
          bold: true,
          margin: [0, 15, 0, 2],
        },
        megaTable: {
          fontSize: 10,
          bold: false,
        },
        collatTable: {
          fontSize: 10,
          bold: false,
          margin: [0, 5, 0, 5],
        },
        detailsValue: {
          margin: [2, 0, 0, 0],
        },
        footerMessage: {
          alignment: 'left',
          margin: [-10, 10],
          width: '100%',
          color: 'lightgrey',
        },
        errorDisplay: {
          margin: [0, 5, 0, 5],
        },
      },
    };

    this.fmLoadingPageService.isLoaderRequesting.set(false);
    pdfMake.createPdf(dd).download(downloadErrorReportName);
  }

  public downloadPDF(pdfData: any): void {
    const downloadedTime = moment(new Date());

    const downloadErrorReportName =
      'Collateral-Analyzer_download_disclosure_' + downloadedTime.format('YYYYMMDDHHmmss') + '.pdf';

    pdfMake.addVirtualFileSystem(pdfFonts);

    const dd = {
      pageOrientation: 'landscape',
      pageSize: 'A4',
      pageMargins: [30, 50, 40, 40],
      header: [
        {
          margin: [10, 5, 0, 5],
          columns: [
            {
              image:
                'data:image/png;base64,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',
              width: 30,
              margin: [20, 0],
              height: 30,
            },
            {
              text: ' Structured Transactions – Collateral Analyzer',
              style: 'header',
            },
            {
              text: 'File created: ' + downloadedTime.format('MM/DD/YYYY HH:mm:ss A'),
              style: 'fileTimestamp',
            },
          ],
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 30,
              y1: 1,
              x2: 794,
              y2: 1,
              lineWidth: 0.5,
            },
          ],
        },
      ],
      footer(currentPage) {
        return {
          margin: 5,
          columns: [
            {
              text: currentPage.toString(),
              margin: [30, 10, 0, 0],
              width: '10%',
            },
            {
              text: 'Disclosure data herein is preliminary and subject to change.',
              style: 'footerMessage',
            },
          ],
        };
      },
      content: this.pdfContent(pdfData),
      styles: {
        header: {
          fontSize: 14,
          bold: false,
          margin: [30, 5, 0, 5],
        },
        fileTimestamp: {
          fontSize: 12,
          bold: false,
          margin: [20, 5, 50, 5],
          color: 'lightgrey',
          alignment: 'right',
        },
        subheaderText: {
          fontSize: 14,
          margin: [0, 0, 0, 15],
        },
        megaHeader: {
          fontSize: 14,
          bold: true,
          margin: [0, 10, 0, 5],
        },
        collateralHeader: {
          fontSize: 14,
          bold: true,
          margin: [0, 15, 0, 2],
        },
        statisticsHeader: {
          fontSize: 12,
          bold: true,
          margin: [0, 15, 0, 2],
        },
        megaTable: {
          fontSize: 10,
          bold: false,
        },
        collatTable: {
          fontSize: 10,
          bold: false,
          margin: [0, 5, 0, 5],
        },
        detailsValue: {
          margin: [2, 0, 0, 0],
        },
        footerMessage: {
          alignment: 'left',
          margin: [-10, 10],
          width: '100%',
          color: 'lightgrey',
        },
        errorDisplay: {
          margin: [0, 5, 0, 5],
        },
      },
    };

    this.fmLoadingPageService.isLoaderRequesting.set(false);
    pdfMake.createPdf(dd).download(downloadErrorReportName);
  }

  public pdfContent(pdfData: any): any {
    let pdfContentDocument: any;

    const collateralValidatedPoolListRes = pdfData.gridData;

    pdfContentDocument = pdfData.showDisclosure
      ? [
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Collateral',
            style: 'collateralHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: [28, 47, 60, 40, 60, 60, 40, 111, 111, 50, 57],

              body: this.parseCollateralDetails(collateralValidatedPoolListRes.collateralDetails),
            },
            pageBreak: 'after',
          },
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Details',
            style: 'collateralHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [268, 477],

              body: this.parseDisclosureDetails(
                pdfData.waDetailsRes,
                pdfData.sellerDetailsRes,
                pdfData.detailsMonthsToMaturity,
                pdfData.detailsNetInterestRate,
              ),
            },
            pageBreak: 'after',
          },
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Statistics',
            style: 'collateralHeader',
          },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 1,
                x2: 762,
                y2: 1,
                lineWidth: 0.5,
              },
            ],
          },
          {
            text: 'Loan Purpose',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseLoanPurposeData(pdfData.loanPurposeRes),
            },
          },
          {
            text: 'Occupancy Status',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseOccupancyStatusData(pdfData.occupancyStatusRes),
            },
          },
          {
            text: 'Channel',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseChannelData(pdfData.channelRes),
            },
          },
          {
            text: 'Property State',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseStateData(pdfData.stateRes),
            },
          },
          {
            text: 'Property Type',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parsePropertyTypeData(pdfData.propertyTypeRes),
            },
          },
          {
            text: 'Quartile Distribution',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [60, 'auto', 46, 65, 36, 40, 60, 60, 80, 60, 52],

              body: this.parseQuartileCompleteData(pdfData.quartileCompleteRes),
            },
            pagebreak: 'before',
          },
          {
            text: 'Number of Borrowers',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseBorrowerData(pdfData.borrowerRes),
            },
          },
          {
            text: 'Mortgage Insurance Cancellation Indicator',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseMtgInsuranceCancelIndData(pdfData.mtgInsuranceCancelIndRes),
            },
          },
          {
            text: 'Mortgage Insurance Coverage',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseMtgInsuranceCoverageData(pdfData.mtgInsuranceCoverageRes),
            },
          },
          {
            text: 'Number of Units',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseNoOfUnitsData(pdfData.numberOfUnitsRes),
            },
          },
          {
            text: 'Origination Year',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseOriginationYearData(pdfData.originationYearRes),
            },
          },
          {
            text: 'Servicer Distribution',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseServicerDistData(pdfData.servicerDistributionRes),
            },
          },
        ]
      : [
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Collateral',
            style: 'collateralHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: [28, 47, 60, 40, 60, 60, 40, 111, 111, 50, 57],

              body: this.parseCollateralDetails(collateralValidatedPoolListRes.collateralDetails),
            },
          },
        ];

    if (pdfData.showDisclosure) {
      if (pdfData.analyzerDetailsTabError) {
        pdfContentDocument = [
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Collateral',
            style: 'collateralHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: [28, 47, 60, 40, 60, 60, 40, 111, 111, 50, 57],

              body: this.parseCollateralDetails(collateralValidatedPoolListRes.collateralDetails),
            },
            pageBreak: 'after',
          },
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Statistics',
            style: 'collateralHeader',
          },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 1,
                x2: 762,
                y2: 1,
                lineWidth: 0.5,
              },
            ],
          },
          {
            text: 'Loan Purpose',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseLoanPurposeData(pdfData.loanPurposeRes),
            },
          },
          {
            text: 'Occupancy Status',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseOccupancyStatusData(pdfData.occupancyStatusRes),
            },
          },
          {
            text: 'Channel',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseChannelData(pdfData.channelRes),
            },
          },
          {
            text: 'Property State',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseStateData(pdfData.stateRes),
            },
          },
          {
            text: 'Property Type',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parsePropertyTypeData(pdfData.propertyTypeRes),
            },
          },
          {
            text: 'Quartile Distribution',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [60, 'auto', 46, 65, 36, 40, 60, 60, 80, 60, 52],

              body: this.parseQuartileCompleteData(pdfData.quartileCompleteRes),
            },
            pagebreak: 'before',
          },
          {
            text: 'Number of Borrowers',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseBorrowerData(pdfData.borrowerRes),
            },
          },
          {
            text: 'Mortgage Insurance Cancellation Indicator',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseMtgInsuranceCancelIndData(pdfData.mtgInsuranceCancelIndRes),
            },
          },
          {
            text: 'Mortgage Insurance Coverage',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseMtgInsuranceCoverageData(pdfData.mtgInsuranceCoverageRes),
            },
          },
          {
            text: 'Number of Units',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseNoOfUnitsData(pdfData.numberOfUnitsRes),
            },
          },
          {
            text: 'Origination Year',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseOriginationYearData(pdfData.originationYearRes),
            },
          },
          {
            text: 'Servicer Distribution',
            style: 'statisticsHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [200, 140, 140, 119, 118],

              body: this.parseServicerDistData(pdfData.servicerDistributionRes),
            },
          },
        ];
      } else if (pdfData.analyzerStatisticsTabError) {
        pdfContentDocument = [
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Collateral',
            style: 'collateralHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: [28, 47, 60, 40, 60, 60, 40, 111, 111, 50, 57],

              body: this.parseCollateralDetails(collateralValidatedPoolListRes.collateralDetails),
            },
            pageBreak: 'after',
          },
          {
            text: 'Mega/Supers Characteristics',
            style: 'megaHeader',
          },
          {
            style: 'megaTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },

            table: {
              headerRows: 1,
              widths: this.megaHeaderWidth,

              body: [
                this.parseMegaHeaders(collateralValidatedPoolListRes.megaType),
                this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails),
              ],
            },
          },
          {
            text: 'Details',
            style: 'collateralHeader',
          },
          {
            style: 'collatTable',
            layout: {
              hLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
              vLineColor(i, _node) {
                return i === 0 ? 'grey' : 'grey';
              },
            },
            table: {
              headerRows: 1,
              widths: [268, 477],

              body: this.parseDisclosureDetails(
                pdfData.waDetailsRes,
                pdfData.sellerDetailsRes,
                pdfData.detailsMonthsToMaturity,
                pdfData.detailsNetInterestRate,
              ),
            },
          },
        ];
      }
    }

    return pdfContentDocument;
  }
}
